import { Component, OnInit, inject, ChangeDetectorRef, signal } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ApiService } from '@core/services/api';
import { KlConfirmDialogService } from '@core/services/kl-confirm-dialog.service';
import { ReportDesignerLaunchService } from '@core/services/report/report-designer-launch.service';
import type { ReportDesignerPayload } from '@core/services/report/report-payload.protocol';
import { ReportViewerLaunchService } from '@core/services/report/report-viewer-launch.service';
import type { FormButton } from '../kl-form-container/kl-form-container';
import Keycloak from 'keycloak-js';

export interface ReceiptPrintConfig {
  sourceData: Record<string, unknown>;
  receipt_design: boolean;
  parentModule: string;
  mainModule: string;
  type: string;
  key: string;
  printApiUrl: string;
}

@Component({
  selector: 'kl-receipt-print',
  standalone: false,
  templateUrl: './kl-receipt-print.html',
  styleUrl: './kl-receipt-print.scss',
})
export class KlReceiptPrint implements OnInit {
  private readonly api = inject(ApiService);
  private readonly confirmDialog = inject(KlConfirmDialogService);
  private readonly keycloak = inject(Keycloak);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ref = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly reportViewerLaunchService = inject(ReportViewerLaunchService);
  private readonly reportDesignerLaunchService = inject(ReportDesignerLaunchService);

  readonly designList = signal<{ id: number; description: string }[]>([]);
  selectedDesign: { id: number; description: string } | null = null;
  readonly loading = signal(false);
  readonly alertMessage = signal('');
  readonly alertShow = signal(false);

  readonly toolbarButtons: FormButton[] = [
    { slug: 'refresh', icon: 'ph ph-arrow-clockwise', method: () => this.refresh() },
    { slug: 'add', icon: 'ph ph-plus', method: () => this.openDesigner('add') },
    {
      slug: 'edit',
      icon: 'ph ph-pencil-simple',
      method: () => this.openDesigner('edit'),
      disabled: () => !this.selectedDesign,
    },
    { slug: 'delete', icon: 'ph ph-trash', method: () => this.deleteDesign(), disabled: () => !this.selectedDesign },
  ];

  private get config(): ReceiptPrintConfig {
    return this.dialogConfig.data as ReceiptPrintConfig;
  }

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading.set(true);
    this.alertShow.set(false);
    this.cdr.markForCheck();

    const userEnv = this.getUserEnv();
    this.api
      .getFilteredData<{ id: number; description: string }[]>('design', {
        parent: { module: this.config.parentModule },
        module: this.config.mainModule,
        key: this.config.key,
        type: this.config.type,
        company_id: userEnv?.company?.id ?? null,
        product_type: 'JRETAIL',
      })
      .subscribe({
        next: (designsResponse) => {
          const list = designsResponse?.data ?? [];
          this.designList.set(list);
          this.selectedDesign = list.length > 0 ? list[0] : null;
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => this.handleError(error),
      });
  }

  print(): void {
    if (!this.selectedDesign) return;
    this.loading.set(true);
    this.cdr.markForCheck();

    this.api.getDataById<{ content?: string } | { content?: string }[]>('design', this.selectedDesign.id).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.cdr.markForCheck();

        // The design endpoint can wrap the record in an array; the viewer needs the object with `content`.
        const currentReport = Array.isArray(response) ? response[0] : response;
        if (!currentReport?.content) {
          this.handleError('error_data');
          return;
        }

        const token = this.keycloak.token;
        const sourceData = this.config.sourceData as Record<string, unknown>;
        const opened = this.reportViewerLaunchService.open({
          currentReport,
          apiUrl: this.config.printApiUrl,
          translateFields: (sourceData['translate_fields'] as string[] | undefined) ?? [],
          dateFields: (sourceData['date_fields'] as string[] | undefined) ?? [],
          authHeaders: token ? { Authorization: 'Bearer ' + token } : {},
        });
        if (!opened) {
          this.handleError('popup_blocked');
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  deleteDesign(): void {
    if (!this.selectedDesign) return;
    const design = this.selectedDesign;

    this.confirmDialog
      .open({
        title: 'entry_must_be_delete',
        description: 'entry_must_be_delete',
        headerLabel: 'warning',
        confirmSeverity: 'danger',
      })
      .subscribe((ok) => {
        if (!ok) return;
        this.loading.set(true);
        this.cdr.markForCheck();

        this.api.deleteData('design', design.id).subscribe({
          next: () => this.refresh(),
          error: (error) => this.handleError(error),
        });
      });
  }

  openDesigner(mode: 'add' | 'edit'): void {
    const cleanSource = { ...this.config.sourceData };
    delete cleanSource['translate_fields'];
    delete cleanSource['date_fields'];
    const userEnv = this.getUserEnv();

    const base: ReportDesignerPayload = {
      mode,
      parentModule: this.config.parentModule,
      module: this.config.mainModule,
      key: this.config.key,
      type: this.config.type,
      company: userEnv?.company ?? undefined,
      receipt_design: this.config.receipt_design,
      sourceData: cleanSource,
    };

    if (mode === 'add') {
      this.openDesignerWindow(base);
      return;
    }

    if (!this.selectedDesign) return;
    this.loading.set(true);
    this.cdr.markForCheck();

    this.api.getDataById<Record<string, unknown>>('design', this.selectedDesign.id).subscribe({
      next: (currentReport) => {
        this.loading.set(false);
        this.cdr.markForCheck();
        base.currentReport = JSON.stringify(currentReport);
        this.openDesignerWindow(base);
      },
      error: (error) => this.handleError(error),
    });
  }

  close(): void {
    this.ref.close();
  }

  private openDesignerWindow(ctx: ReportDesignerPayload): void {
    const opened = this.reportDesignerLaunchService.open(ctx);
    if (!opened) {
      this.handleError('popup_blocked');
    }
  }

  private handleError(error: unknown): void {
    this.loading.set(false);
    this.alertMessage.set(
      typeof error === 'string' ? error : ((error as any)?.error?.message ?? (error as any)?.message ?? 'error')
    );
    this.alertShow.set(true);
    this.cdr.markForCheck();
  }

  private getUserEnv(): { company?: { id: number } } {
    try {
      const raw = sessionStorage.getItem('userEnv');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }
}
